import React, { useMemo } from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardMetricItem from '@components/pages/Dashboard/DashboardMetricItem.component'
import DashboardHalfDonutChart from '@components/pages/Dashboard/MetricCharts/DashboardHalfDonutChart.component'
import type { DashboardUtilizationData } from '@hooks/dashboard/useDashboardUtilizationData'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  buildCaseAcceptanceDonutFromOverview,
  mapCaseAcceptanceOverviewToMetrics,
} from '@utils/treatmentAcceptance.util'
import {
  isDashboardDevMocksEnabled,
  withDashboardMetricShellValues,
} from '@utils/dashboardDevMocks.util'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { metricDataToItemPropsWithLayoutClass } from '@utils/dashboardMetrics.util'
import { getOverviewCaseAcceptanceMetrics } from '../overviewMetrics.mockData'

type CaseAcceptanceCardProps = {
  range: RangeKey
  titleHref: string
  caseAcceptanceOverview: DashboardUtilizationData['caseAcceptanceOverview']
  loading: boolean
}

export function CaseAcceptanceCard({
  range,
  titleHref,
  caseAcceptanceOverview,
  loading,
}: CaseAcceptanceCardProps): React.ReactElement {
  const useMocks = isDashboardDevMocksEnabled()
  const donut = useMemo(
    () =>
      buildCaseAcceptanceDonutFromOverview(caseAcceptanceOverview?.kpis, range),
    [caseAcceptanceOverview?.kpis, range],
  )
  const metrics = useMemo(() => {
    if (caseAcceptanceOverview?.kpis) {
      return mapCaseAcceptanceOverviewToMetrics(caseAcceptanceOverview.kpis)
    }
    return withDashboardMetricShellValues(
      getOverviewCaseAcceptanceMetrics(range),
      useMocks,
    )
  }, [caseAcceptanceOverview?.kpis, range, useMocks])
  // Effective rendered data: response KPIs when present, otherwise the metric
  // shells (blank when mocks are off) and the fallback donut.
  const hasData = caseAcceptanceOverview
    ? hasNonZeroDashboardValues(
        caseAcceptanceOverview.kpis.primaryValue,
        caseAcceptanceOverview.kpis.acceptedVal,
        caseAcceptanceOverview.kpis.sameDayVal,
        caseAcceptanceOverview.kpis.pendingVal,
      )
    : hasNonZeroDashboardValues(
        ...metrics.map((metric) => metric.value),
        donut.primaryValue,
      )
  const isEmpty = shouldShowDashboardSectionEmpty(loading, hasData)
  const pending = isDashboardSectionPending(
    loading,
    caseAcceptanceOverview != null,
  )

  return (
    <DashboardSectionCard
      title="Case acceptance"
      titleHref={titleHref}
      className="dashboard-acceptance-card"
      loading={pending}
      skeleton={{ metrics: 3, block: 'chart' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
    >
      <div className="dashboard-overview-acceptance-content">
        <div className="dashboard-overview-acceptance-metrics-row">
          {metrics.map((metric) => (
            <DashboardMetricItem
              key={metric.id ?? metric.leftLabel}
              {...metricDataToItemPropsWithLayoutClass(
                metric,
                'dashboard-overview-acceptance-metric',
              )}
            />
          ))}
        </div>
        <DashboardMetricItem
          className="dashboard-overview-acceptance-metric dashboard-overview-acceptance-metric--full"
          leftLabel="Avg case acceptance"
          value={
            <>
              {donut.valueFormatter(donut.primaryValue)}{' '}
              <span className="dashboard-metric-value-vs">vs</span>{' '}
              <span className="dashboard-metric-value-over">
                {donut.valueFormatter(donut.secondaryValue)}
              </span>
            </>
          }
        />
        <div className="dashboard-overview-acceptance-chart-box">
          <DashboardHalfDonutChart
            title="Case acceptance"
            primaryValue={donut.primaryValue}
            secondaryValue={donut.secondaryValue}
            valueFormatter={donut.valueFormatter}
            slices={donut.slices}
            hideCenterMetric
            className="dashboard-overview-acceptance-chart"
          />
          <div className="dashboard-overview-acceptance-legend">
            <span className="dashboard-overview-acceptance-legend-item">
              <span
                className="dashboard-overview-acceptance-legend-dot"
                style={{ background: 'var(--blue-400)' }}
              />
              Acceptance rate
            </span>
            <span className="dashboard-overview-acceptance-legend-item">
              <span
                className="dashboard-overview-acceptance-legend-dot"
                style={{ background: 'var(--orange-300)' }}
              />
              Not accepted
            </span>
          </div>
        </div>
      </div>
    </DashboardSectionCard>
  )
}
